/**
 * A playlist's DJ-app CONNECTIONS — the tools it currently syncs with.
 *
 * A playlist can be linked to several apps at once (playlist_sources,
 * migration 011) and nothing limits that: imported from Serato AND synced to
 * Traktor is a normal state. The cloud rows the sidebar renders only carry
 * the description string ("Imported from <App>: …"), which is single-valued
 * provenance — so the live links come from the local DB over IPC
 * (db:playlists:getSourceLinks) and the description is the FALLBACK for rows
 * the local DB doesn't know (web app, signed-out, stale mapping).
 *
 * "mixgoat" is not a connection — it's the ABSENCE of any: a cloud-only
 * playlist. Filters treat it as its own bucket.
 */

export const CONNECTION_APPS = ['rekordbox', 'serato', 'traktor', 'virtualdj'];

/** Single-valued provenance from the import description. Null when none. */
export function importSourceOf(p) {
    const d = p?.description || '';
    if (d.includes('Imported from Rekordbox')) return 'rekordbox';
    if (d.includes('Imported from Serato')) return 'serato';
    if (d.includes('Imported from Traktor')) return 'traktor';
    if (d.includes('Imported from Virtual DJ')) return 'virtualdj';
    return null;
}

/**
 * Apps this playlist syncs with. `links` maps THIS list's row ids (server
 * ids for the cloud-backed list, local ids otherwise) to app arrays; a row
 * absent from the map falls back to its description.
 */
export function connectionsOf(p, links) {
    const linked = p && links && links[p.id];
    if (Array.isArray(linked) && linked.length) {
        return linked.filter((s) => CONNECTION_APPS.includes(s));
    }
    const d = importSourceOf(p);
    return d ? [d] : [];
}

/** OR-filter: 'mixgoat' matches only unconnected (cloud-only) playlists. */
export function matchesConnection(p, links, key) {
    const c = connectionsOf(p, links);
    return key === 'mixgoat' ? c.length === 0 : c.includes(key);
}

/**
 * Per-bucket counts. Multi-connected playlists count once per connected app
 * (OR-filter semantics), so DJ buckets may sum over the total — that's the
 * honest reading, not a bug.
 */
export function connectionCounts(playlists, links) {
    const counts = { mixgoat: 0, rekordbox: 0, serato: 0, traktor: 0, virtualdj: 0 };
    for (const p of playlists || []) {
        const c = connectionsOf(p, links);
        if (!c.length) { counts.mixgoat++; continue; }
        for (const app of new Set(c)) counts[app]++;
    }
    return counts;
}
